import React, { useEffect } from 'react';
import { Link, RouteComponentProps, useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { Checkbox, FormInputErrorMessage } from '@clearcover/atlas';
import urlJoin from 'url-join';

import {
  Body,
  Footer,
  FooterNextButton,
  FooterPreviousButton,
  ScreenHeader,
} from 'QuoteApp/components';
import { useAppContext } from 'QuoteApp/services/providers';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';
import { useLazyQuoteApi } from 'Shared/services/Api';

import { Statements } from './Statements';
import { Schema } from './Schema';
import { UnderwritingFormValues } from './types';
import styles from './styles.module.css';

export const Underwriting: React.FC<RouteComponentProps> = () => {
  const router = useHistory();
  const {
    appId,
    details,
    canNavigate,
    changeCanNavigate,
    refreshDetails,
    dictionary,
  } = useAppContext();

  const underwritingConsentsMarkdown = dictionary?.underwritingConsent;

  const [{ loading: completeUWLoading }, postAcceptUW] = useLazyQuoteApi({
    method: 'POST',
    url: `/insurance_applications/${appId}/underwriting_consent/complete`,
  });

  const [{ loading: unacceptUWLoading }, postUnacceptUW] = useLazyQuoteApi({
    method: 'POST',
    url: `/insurance_applications/${appId}/underwriting_consent/unaccept`,
  });

  const isUWAgreementAccepted = !!details?.underwritingConsent?.givenAt;

  const initialValues: UnderwritingFormValues = {
    consent: isUWAgreementAccepted,
  };

  const goToReports = () => {
    router.push(urlJoin(QUOTE_APP_ROUTE, appId, '/reports'));
  };

  const goToSummary = () => {
    router.push(urlJoin(QUOTE_APP_ROUTE, appId, '/summary'));
  };

  const acceptAgreement = async () => {
    const completeCommand = details.commands.completeUnderwritingConsent;

    if (completeCommand?.runnable) {
      await postAcceptUW();
      await refreshDetails();
      return;
    }

    return Promise.reject(new Error('Failed to accept agreement'));
  };

  const unacceptAgreement = async () => {
    const uncompleteCommand = details.commands.unacceptUnderwritingConsent;

    if (uncompleteCommand?.runnable) {
      await postUnacceptUW();
      await refreshDetails();
      return;
    }

    return Promise.reject(new Error('Failed to unaccept agreement'));
  };

  useEffect(() => {
    changeCanNavigate(isUWAgreementAccepted);
  }, [changeCanNavigate, isUWAgreementAccepted]);

  return (
    <>
      <Body data-testid="underwriting-page">
        <ScreenHeader
          hideQuotePdfLink
          headerText="Before continuing, you must confirm the following information is true"
        />
        <Statements markdown={underwritingConsentsMarkdown} />
        <div className={styles.makeChanges}>
          <p>Need to make a change?</p>{' '}
          <Link
            className={styles.contactInfoEmailEditLink}
            to={urlJoin(QUOTE_APP_ROUTE, appId, '/drivers')}
          >
            Add Drivers
          </Link>
          {', '}
          <Link
            className={styles.contactInfoEmailEditLink}
            to={urlJoin(QUOTE_APP_ROUTE, appId, '/drivers')}
          >
            Excluded Drivers
          </Link>
          {', or '}
          <Link
            className={styles.contactInfoEmailEditLink}
            to={urlJoin(QUOTE_APP_ROUTE, appId, '/vehicles')}
          >
            Update Vehicles
          </Link>
        </div>
        <Formik
          enableReinitialize
          validationSchema={Schema}
          initialValues={initialValues}
          onSubmit={(formData, { setSubmitting, setFieldError }) => {
            const { consent } = formData;

            if (consent) {
              acceptAgreement()
                .then(() => {
                  changeCanNavigate(true);
                })
                .catch(() => {
                  setFieldError(
                    'consent',
                    'There was an error accepting the agreement. Please try again.'
                  );
                  changeCanNavigate(false);
                })
                .finally(() => {
                  setSubmitting(false);
                });
            } else {
              unacceptAgreement().finally(() => {
                changeCanNavigate(false);
                setFieldError(
                  'consent',
                  'Check the box to confirm before continuing.'
                );
                setSubmitting(false);
              });
            }
          }}
        >
          {({ values, errors, isSubmitting, setFieldValue, submitForm }) => {
            return (
              <div className={styles.formContainer}>
                <Checkbox
                  data-testid="underwriting-consent"
                  name="consent"
                  label="I confirm this information has been verified with the applicant, and that they agree to all of the statements above."
                  value={values.consent}
                  className={styles.checkboxAgreement}
                  disabled={
                    isSubmitting || completeUWLoading || unacceptUWLoading
                  }
                  onChange={(checked) => {
                    setFieldValue('consent', checked);
                    submitForm();
                  }}
                />
                {!!errors.consent && (
                  <FormInputErrorMessage
                    message={errors.consent || 'Required'}
                    className={styles.underwritingError}
                    name="consent"
                  />
                )}
              </div>
            );
          }}
        </Formik>
      </Body>
      <Footer
        hideRate
        previousButton={
          <FooterPreviousButton onClick={goToSummary}>
            Back
          </FooterPreviousButton>
        }
        nextButton={
          <FooterNextButton
            disabled={!canNavigate || completeUWLoading || unacceptUWLoading}
            onClick={goToReports}
          >
            Next: Reports
          </FooterNextButton>
        }
      />
    </>
  );
};
